import React, { ReactNode, useState } from 'react'
import { Dialog } from '@base-ui/react/dialog'
import WeekDatePicker from './WeekDatePicker'
import TimeSlotPicker from './TimeSlotPicker'
import ConfirmationScreen from './ConfirmationScreen'
import SelfScheduleModalHeader from './SelfScheduleModalHeader'
import SelfScheduleModalFooter from './SelfScheduleModalFooter'
import { useSelfSchedule } from './useSelfSchedule'
import { useBodyScrollLock } from './useBodyScrollLock'
import { useFramedModalViewport } from './useFramedModalViewport'
import { isFramed } from '../../../utils/isFramed'
import './SelfScheduleModal.scss'

interface SelfScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  advisorProfile?: ReactNode
  onConfirm: (date: Date, time: string) => void | Promise<void>
}

const SUBMIT_ERROR_MESSAGE = "We couldn't schedule your call. Please try again."

function SelfScheduleModal ({ isOpen, onClose, advisorProfile, onConfirm }: SelfScheduleModalProps) {
  const {
    selectedDate,
    selectedTime,
    status,
    isSubmitting,
    showConfirmation,
    isConfirmDisabled,
    selectDate,
    selectTime,
    confirm,
    reset
  } = useSelfSchedule(onConfirm)

  useBodyScrollLock(isOpen)

  // base-ui's Dialog.Portal mounts its container a render after this component's own
  // effects run, so a plain ref would still read null when the hook below needs it.
  const [popup, setPopup] = useState<HTMLDivElement | null>(null)
  useFramedModalViewport(isOpen, popup)

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset on any close (Escape, outside press, or close button).
      reset()
      onClose()
    }
  }

  const contentClassName = isFramed() ? 'self_schedule_modal_content match-screen-app--framed' : 'self_schedule_modal_content'
  const pickerSectionsClassName = isFramed() ? 'self_schedule_modal_picker_sections match-screen-app--framed' : 'self_schedule_modal_picker_sections'

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal="trap-focus">
      <Dialog.Portal>
        <Dialog.Backdrop className="self_schedule_modal_backdrop" />
        <Dialog.Viewport className="self_schedule_modal_viewport">
          <Dialog.Popup ref={setPopup} className="self_schedule_modal_container">
            <SelfScheduleModalHeader showConfirmation={showConfirmation} advisorProfile={advisorProfile} />
            <div className={contentClassName}>
              {showConfirmation && selectedDate && selectedTime
                ? <ConfirmationScreen date={selectedDate} time={selectedTime} />
                : (
                  <div className={pickerSectionsClassName}>
                    <WeekDatePicker selectedDate={selectedDate} onSelectDate={selectDate} />
                    <TimeSlotPicker selectedDate={selectedDate} selectedTime={selectedTime} onSelectTime={selectTime} />
                  </div>
                  )}
            </div>
            {status === 'error' && <p className="self_schedule_modal_error" role="alert">{SUBMIT_ERROR_MESSAGE}</p>}
            {!showConfirmation && (
              <SelfScheduleModalFooter isSubmitting={isSubmitting} disabled={isConfirmDisabled} onConfirm={confirm} />
            )}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default SelfScheduleModal
